// EDEN · the GTIS lesson-procedure steps — serialize/parse for the single `plan.procedure` column.
//
// The school's plan template fixes FIVE named steps. The teacher types each in their own English
// (paste-locked). To stay backward-compatible we keep storing the one `plan.procedure` text column:
// the five steps are serialized as labeled blocks ("1 Class Admin:\n…"), and parsed back for editing
// + the paper-faithful view. A plan whose procedure doesn't match the markers (a pre-PROMPT-3 plan,
// or the legacy combined `body`) is treated as ONE legacy block — never silently split or lost.
//
// The label list + format is the shared contract; a mirror parser lives in
// functions/api/_lib/template.ts (the src/ and functions/ trees don't share modules). Keep them
// in sync — the markers below are the source of truth.

export type StepKey = "class_admin" | "review" | "new_lesson" | "consolidation" | "homework";

export interface Step {
  key: StepKey;
  /** The serialized marker label, e.g. "1 Class Admin" (the line stored as "1 Class Admin:"). */
  label: string;
  /** One-line friendly guidance under the box (English — it's the template's own structure). */
  hint: string;
}

// Fixed order + labels. The leading number is part of the marker, so parsing is unambiguous.
export const STEPS: Step[] = [
  { key: "class_admin", label: "1 Class Admin", hint: "Attendance, books out, settle the room — note anything specific." },
  { key: "review", label: "2 Review", hint: "A quick warm-up that connects to what they already know." },
  { key: "new_lesson", label: "3 New Lesson", hint: "The heart of the lesson — present, then the activities, step by step." },
  { key: "consolidation", label: "4 Consolidation", hint: "Pull it together — the misconception + check you wrote above feed this step." },
  { key: "homework", label: "5 Homework", hint: "A small task that practises today — nothing heavy." },
];

export type StepTexts = Record<StepKey, string>;

// ── The typed floor (owner ruling 2026-09-03): a plan may be SUBMITTED only when the teacher has
// written the key activities and knowledge in their own words. Drafts are always free. The floor is
// counted in characters of the teacher's own text so a heading alone, or a lone word, does not pass.
// Friendly wording, one field at a time, never a red wall (Design Brief) — see `firstShortfall`.

export const STEP_FLOOR: Record<StepKey, number> = {
  class_admin: 15,
  review: 40,
  new_lesson: 120,
  consolidation: 60,
  homework: 15,
};

export const CELL_FLOOR = { prior: 25, misconception: 25, check_for_understanding: 25 } as const;
export type CellKey = keyof typeof CELL_FLOOR;

/** Characters that count: letters/digits, collapsed whitespace — punctuation-only filler does not. */
export function typedLength(text: string | null | undefined): number {
  return (text ?? "").replace(/[^\p{L}\p{N}]+/gu, " ").trim().length;
}

export interface Shortfall {
  field: CellKey | StepKey;
  kind: "cell" | "step";
  /** The template's own label — "3 New Lesson", "Prior knowledge" — for the friendly line. */
  label: string;
  need: number;
  have: number;
}

/** The first field below its floor, in template order (cells, then steps 1–5), or null when the
 *  plan carries enough of the teacher's own writing to go to the Lead. */
export function firstShortfall(
  cells: Record<CellKey, string | null | undefined>,
  steps: Partial<StepTexts>,
  cellLabels: Record<CellKey, string>,
): Shortfall | null {
  for (const key of Object.keys(CELL_FLOOR) as CellKey[]) {
    const have = typedLength(cells[key]);
    if (have < CELL_FLOOR[key]) return { field: key, kind: "cell", label: cellLabels[key], need: CELL_FLOOR[key], have };
  }
  for (const s of STEPS) {
    const have = typedLength(steps[s.key]);
    if (have < STEP_FLOOR[s.key]) return { field: s.key, kind: "step", label: s.label, need: STEP_FLOOR[s.key], have };
  }
  return null;
}

const emptyTexts = (): StepTexts => ({ class_admin: "", review: "", new_lesson: "", consolidation: "", homework: "" });

/** Serialize the five boxes into one labeled-block string. Empty steps still emit their header so
 *  the structure round-trips (and an all-empty procedure serializes to just the five headers). */
export function serializeSteps(texts: StepTexts): string {
  return STEPS.map((s) => `${s.label}:\n${(texts[s.key] ?? "").trim()}`).join("\n\n");
}

/** Parse a stored procedure back into the five boxes. Returns { legacy } when the text doesn't
 *  carry all five fixed markers in order (old plans / the legacy `body`) so the caller shows one box. */
export function parseSteps(procedure: string | null | undefined): { steps: StepTexts } | { legacy: string } {
  const raw = (procedure ?? "").trim();
  if (!raw) return { steps: emptyTexts() }; // empty draft → five empty boxes

  // Locate each "N Label:" marker line. matchStart = the marker's first char; contentStart = just
  // past its header line (where that step's text begins).
  const marks: { key: StepKey; matchStart: number; contentStart: number }[] = [];
  for (const s of STEPS) {
    const label = s.label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const m = new RegExp(`(?:^|\\n)${label}:[ \\t]*\\r?\\n?`).exec(raw);
    if (!m) continue;
    const lead = m[0].startsWith("\n") ? 1 : 0;
    marks.push({ key: s.key, matchStart: m.index + lead, contentStart: m.index + m[0].length });
  }
  // Need ALL five markers, in canonical order, to treat this as structured; otherwise it's legacy.
  if (marks.length !== STEPS.length) return { legacy: raw };
  marks.sort((a, b) => a.matchStart - b.matchStart);
  if (marks.map((x) => x.key).join() !== STEPS.map((s) => s.key).join()) return { legacy: raw };

  // Each step's text runs from its contentStart up to the next marker (or end of string).
  const out = emptyTexts();
  marks.forEach((mk, i) => {
    const end = i + 1 < marks.length ? marks[i + 1]!.matchStart : raw.length;
    out[mk.key] = raw.slice(mk.contentStart, end).trim();
  });
  return { steps: out };
}
